import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from "./context/AuthContext/AuthContext";
import PrivateWrapper from "./components/PrivateWrapper/PrivateWrapper";
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import PublicWrapper from './components/PublicWrapper/PublicWrapper';
import { Navigate } from 'react-router-dom';
import { LocationProvider } from './context/LocationContext/LocationContext';
import { PageProvider } from './context/PageContext/PageContext';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorFallback';
import SuspenseFallback from './components/SuspenseFallback';
import RootRoute from './components/RootRoute';
import AccountUsersPage from './pages/Dashboard/AccountUsersPage';
import MyPropertiesPage from './pages/MyProperties';
import BulkUploadPage from './pages/BulkUpload';

// Lazy-loaded components

// Authentication and User Management
const AccountInReviewPage = lazy(() => import('./pages/AccountInReviewPage/AccountInReviewPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage/RegisterPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage/PasswordResetPage'));
const PasswordResetConfirmPage = lazy(() => import('./pages/PasswordResetConfirmPage/PasswordResetConfirmPage'));
const OnboardingEmailVerify = lazy(() => import('./pages/Onboarding/OnboardingEmailVerify'));

// Dashboard and Admin
const PropertyApprovalTablePage = lazy(() => import('./pages/Admin/PropertyApprovalTablePage'));
const PropertyApprovalPage = lazy(() => import('./pages/Admin/PropertyApprovalPage'));
const CompanyPage = lazy(() => import('./pages/ProfilePage/CompanyPage'));
const AmenitiesPage = lazy(() => import('./pages/Admin/AmenitiesPage'));
const UserDetailsPage = lazy(() => import('./pages/Dashboard/UserDetailsPage'));
const AccountApprovalPage = lazy(() => import('./pages/Admin/AccountApprovalPage'));
const AccountApprovalTablePage = lazy(() => import('./pages/Admin/AccountApprovalTablePage'));
// PP-675 — carrier-COI review queue, independent of AllInsurancePage below.
const InsuranceApprovalPage = lazy(() => import('./pages/Admin/InsuranceApprovalPage'));
const InsuranceApprovalTablePage = lazy(() => import('./pages/Admin/InsuranceApprovalTablePage'));

// Property Management
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizard/OnboardingWizardPage'));
const PropertyPage = lazy(() => import('./pages/PropertyPage/PropertyPage'));

// Fleet Management
const NewFleetPage = lazy(() => import('./pages/NewFleetPage/NewFleetPage'));
const FleetMemberListPage = lazy(() => import('./pages/FleetMemberListPage/FleetMemberListPage'));
const DriverOnboardingPage = lazy(() => import('./pages/DriverOnboardingPage/DriverOnboardingPage'));
const EditFleetPage = lazy(() => import('./pages/EditFleetPage/EditFleetPage'));
const DashboardFleetsPage = lazy(() => import('./pages/DashboardFleetsPage/DashboardFleetsPage'));

// Booking and Reservations
const PropertyBookPage = lazy(() => import('./pages/PropertyBookPage/PropertyBookPage'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage/ReservationsPage'));
const PaymentMethodsPage = lazy(() => import('./pages/Dashboard/PaymentMethodsPage'));
const ReservationDetailsPage = lazy(() => import('./pages/ReservationDetailsPage/ReservationDetailsPage'));
const PayoutDetailsPage = lazy(() => import('./pages/Dashboard/PayoutDetailsPage'));

// Miscellaneous
const MapSearchPage = lazy(() => import('./pages/MapSearchPage/MapSearchPage'));
const Styleguide = lazy(() => import('./pages/Styleguide/index'));
const UsersPage = lazy(() => import('./pages/SuperAdmin/UsersPage'));
const AdminUserPage = lazy(() => import('./pages/SuperAdmin/AdminUserPage'));
const PropertiesPage = lazy(() => import('./pages/SuperAdmin/PropertiesPage'));
const AdminPropertyPage = lazy(() => import('./pages/SuperAdmin/AdminPropertyPage'));
const AllReservationsPage = lazy(() => import('./pages/SuperAdmin/AllReservationsPage'));
const ComingSoonPage = lazy(() => import('./pages/SuperAdmin/ComingSoonPage'));
const FleetsPage = lazy(() => import('./pages/SuperAdmin/FleetsPage'));
const AllInsurancePage = lazy(() => import('./pages/SuperAdmin/AllInsurancePage'));
const InsurancePolicyPage = lazy(() => import('./pages/CarrierAdmin/InsurancePolicyPage'));


const App = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthProvider>
        <LocationProvider>
          <>
            <Router>
            <PageProvider>
              <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/find-parking" element={<MapSearchPage />} />
                  <Route path="/login" element={<MapSearchPage />} />
                  <Route path="/host" element={<Navigate replace to="/host/register" />} />
                  <Route path="/in-review" element={<AccountInReviewPage />} />
                  <Route path="/:role?/register" element={<PublicWrapper><RegisterPage /></PublicWrapper>} />
                  <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
                  <Route path="/password-reset/:token?" element={<PasswordResetPage />} />
                  <Route path="/onboarding/email" element={<PrivateWrapper><OnboardingEmailVerify /></PrivateWrapper>} />
                  <Route path="/onboarding/driver" element={<PrivateWrapper><DriverOnboardingPage /></PrivateWrapper>} />
                  <Route path="/profile" element={<PrivateWrapper><ProfilePage /></PrivateWrapper>} />
                  <Route path="/company" element={<PrivateWrapper><CompanyPage /></PrivateWrapper>} />
                  <Route path="/property/:propertyId/:review?" element={<PropertyPage />} />
                  <Route path="/book/:propertyId" element={<PrivateWrapper><PropertyBookPage /></PrivateWrapper>} />
                  <Route path="/book/confirmation/:reservationId" element={<PrivateWrapper><ReservationDetailsPage /></PrivateWrapper>} />
                  <Route path="/styleguide" element={<Styleguide />} />

                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/dashboard/*" element={
                    <PrivateWrapper>
                      <Routes>
                        <Route path="amenities" element={<AmenitiesPage />} />
                        <Route path="account/users" element={<AccountUsersPage />} />
                        <Route path="accounts/approval/:accountId" element={<AccountApprovalPage />} />
                        <Route path="accounts/approval" element={<AccountApprovalTablePage />} />
                        {/* PP-696 — driver COIs get their own detail path (2 segments) so
                            React Router's specificity ranking always prefers it over the
                            plain :coiId route below for a "driver/<id>" URL, regardless of
                            declaration order. The plain route stays the carrier path (no
                            `type` prop passed) so every existing carrier COI link/bookmark
                            keeps working unchanged. */}
                        <Route path="insurance/approval/driver/:coiId" element={<InsuranceApprovalPage type="driver" />} />
                        <Route path="insurance/approval/:coiId" element={<InsuranceApprovalPage />} />
                        <Route path="insurance/approval" element={<InsuranceApprovalTablePage />} />
                        <Route path="listings/new/:stepIndex?" element={<OnboardingWizardPage />} />
                        <Route path="listings/edit/:listingId/:stepIndex?" element={<OnboardingWizardPage />} />
                        <Route path="listings/bulk" element={<BulkUploadPage />} />
                        <Route path="properties/approval/:propertyId" element={<PropertyApprovalPage />} />
                        <Route path="properties/approval" element={<PropertyApprovalTablePage />} />
                        <Route path="my-properties" element={<MyPropertiesPage />} />
                        <Route path="my-properties/:listingId" element={<MyPropertiesPage />} />
                        <Route path="fleet/edit/:fleetId/:stepIndex?" element={<EditFleetPage />} />
                        <Route path="fleet/:fleetId/user/:userId" element={<UserDetailsPage />} />
                        <Route path="fleet/:fleetId/*" element={<FleetMemberListPage />} />
                        <Route path="fleet/new/:stepIndex?" element={<NewFleetPage />} />
                        <Route path="fleet/add-driver" element={<DashboardFleetsPage />} />
                        <Route path="fleet" element={<DashboardFleetsPage />} />
                        <Route path="insurance-policy" element={<InsurancePolicyPage />} />
                        <Route path="reservations/:reservationId" element={<ReservationDetailsPage />} />
                        <Route path="reservations" element={<ReservationsPage />} />
                        <Route path="payment-methods/*" element={<PaymentMethodsPage />} />
                        <Route path="payout-details/*" element={<PayoutDetailsPage />} />
                        <Route path="super-admin/users" element={<UsersPage />} />
                        <Route path="super-admin/users/:userId" element={<AdminUserPage />} />
                        <Route path="super-admin/properties" element={<PropertiesPage />} />
                        <Route path="super-admin/properties/:propertyId" element={<AdminPropertyPage />} />
                        <Route path="super-admin/fleets" element={<FleetsPage />} />
                        <Route path="super-admin/coi" element={<AllInsurancePage />} />
                        <Route path="super-admin/reservations" element={<AllReservationsPage />} />
                        <Route path="super-admin/reservations/:reservationId" element={<ReservationDetailsPage />} />
                        <Route path="super-admin/coming-soon" element={<ComingSoonPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </PrivateWrapper>
                  } />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </PageProvider>
          </Router>
          <ToastContainer />
          </>
        </LocationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;